import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import {
  convertCoordinatesToLatLng,
  convertLatLngToCoordinates,
} from "@mapmycustomers/shared/util/geo/GeoService";

import pinDropIcon from "@app/assets/map/pinDrop.svg";
import pinDropDraggingIcon from "@app/assets/map/pinDropDragging.svg";
import { useMap } from "@app/component/map/FeaturedMap";
import useMapView from "@app/component/view/utils/useMapView";
import { updatePinDropCoordinates } from "@app/scene/map/store/actions";
import { getMapPinDrop } from "@app/scene/map/store/selectors";
import PinDropModel from "@app/scene/map/types/PinDropModel";
import { RootState } from "@app/store/rootReducer";

interface Props {
  pinDrop?: PinDropModel;
  updatePinDropCoordinates: typeof updatePinDropCoordinates.request;
}

const PIN_DROP_ICON_WIDTH = 50;
const PIN_DROP_ICON_HEIGHT = 71;
const PIN_DROP_ICON_ANCHOR_LEFT = PIN_DROP_ICON_WIDTH / 2;
const PIN_DROP_ICON_ANCHOR_TOP = 62;
const PIN_DROP_ICON_ACTIVE_OFFSET = 3;

const DropPinMarker: React.FC<Props> = ({ pinDrop, updatePinDropCoordinates }) => {
  const { map } = useMap();
  const { viewState } = useMapView();
  const markerRef = useRef<google.maps.Marker | undefined>();

  useEffect(
    () => {
      const listeners: google.maps.MapsEventListener[] = [];

      const PINDROP_MARKER = {
        anchor: new google.maps.Point(PIN_DROP_ICON_ANCHOR_LEFT, PIN_DROP_ICON_ANCHOR_TOP),
        scaledSize: new google.maps.Size(PIN_DROP_ICON_WIDTH, PIN_DROP_ICON_HEIGHT),
        url: pinDropIcon,
      };

      const PINDROP_MARKER_DRAGGING = {
        anchor: new google.maps.Point(
          PIN_DROP_ICON_ANCHOR_LEFT,
          PIN_DROP_ICON_ANCHOR_TOP + PIN_DROP_ICON_ACTIVE_OFFSET
        ),
        scaledSize: new google.maps.Size(PIN_DROP_ICON_WIDTH, PIN_DROP_ICON_HEIGHT),
        url: pinDropDraggingIcon,
      };

      if (viewState.tool === MapTool.DROP_PIN) {
        const position: google.maps.LatLng = pinDrop?.coordinates
          ? convertCoordinatesToLatLng(pinDrop.coordinates)
          : map.getCenter()!;

        markerRef.current = new google.maps.Marker({
          animation: google.maps.Animation.DROP,
          crossOnDrag: false,
          draggable: true,
          icon: PINDROP_MARKER,
          map,
          position: position,
          visible: true,
        });

        listeners.push(
          markerRef.current.addListener("click", () => {
            const markerPosition = markerRef.current?.getPosition();
            if (markerPosition) {
              updatePinDropCoordinates(convertLatLngToCoordinates(markerPosition));
            }
          })
        );

        listeners.push(
          markerRef.current.addListener("mousedown", () => {
            markerRef.current?.setIcon(PINDROP_MARKER_DRAGGING);
          })
        );

        listeners.push(
          markerRef.current.addListener("mouseup", () => {
            markerRef.current?.setIcon(PINDROP_MARKER);
          })
        );

        listeners.push(
          markerRef.current.addListener("dragend", (event: google.maps.MapMouseEvent) => {
            if (!event.latLng) {
              return;
            }
            updatePinDropCoordinates(convertLatLngToCoordinates(event.latLng));
          })
        );

        updatePinDropCoordinates(convertLatLngToCoordinates(position));
      }

      return () => {
        if (markerRef.current) {
          listeners.forEach((listener) => listener.remove());

          markerRef.current.setMap(null);
          markerRef.current = undefined;

          updatePinDropCoordinates(undefined);
        }
      };
    },
    // we don't have pinDrop?.coordinates in deps because we only need it during first call,
    // when tool becomes visible. We don't care about further changes in pinDrop.coordinates
    // because this exact marker (markerRef) is responsible for pinDrop.coordinates updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, updatePinDropCoordinates, viewState.tool]
  );

  useEffect(() => {
    if (pinDrop?.coordinates) {
      markerRef.current?.setPosition(convertCoordinatesToLatLng(pinDrop.coordinates));
    }
  }, [pinDrop?.coordinates]);

  return null;
};

const mapStateToProps = (state: RootState) => ({
  pinDrop: getMapPinDrop(state),
});

const mapDispatchToProps = {
  updatePinDropCoordinates: updatePinDropCoordinates.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DropPinMarker);
